/**
 * useDashboardPreferences Hook
 *
 * Custom hook for managing dashboard section visibility preferences.
 * Persists preferences to localStorage for offline access and instant persistence.
 */
import { useState, useEffect, useCallback } from 'react';
import { DEFAULT_PREFERENCES, DEFAULT_SECTION_VISIBILITY, STORAGE_KEYS } from '../types/dashboardPreferences';
export const useDashboardPreferences = (dashboardType) => {
    const storageKey = dashboardType === 'financial'
        ? STORAGE_KEYS.FINANCIAL_DASHBOARD
        : STORAGE_KEYS.INVENTORY_DASHBOARD;
    // Initialize state from localStorage
    const [preferences, setPreferences] = useState(() => {
        try {
            const stored = localStorage.getItem(storageKey);
            if (stored) {
                const parsed = JSON.parse(stored);
                // Ensure all keys exist (in case new sections are added)
                return {
                    ...DEFAULT_PREFERENCES,
                    ...parsed,
                    sectionVisibility: {
                        ...DEFAULT_SECTION_VISIBILITY,
                        ...parsed.sectionVisibility
                    }
                };
            }
        }
        catch (error) {
            console.warn('Failed to load dashboard preferences from localStorage:', error);
        }
        return DEFAULT_PREFERENCES;
    });
    // Persist to localStorage whenever preferences change
    useEffect(() => {
        try {
            localStorage.setItem(storageKey, JSON.stringify(preferences));
        }
        catch (error) {
            console.warn('Failed to save dashboard preferences to localStorage:', error);
        }
    }, [preferences, storageKey]);
    // Toggle a specific section
    const toggleSection = useCallback((section) => {
        setPreferences(prev => ({
            ...prev,
            sectionVisibility: {
                ...prev.sectionVisibility,
                [section]: !prev.sectionVisibility[section]
            },
            lastUpdated: new Date().toISOString()
        }));
    }, []);
    // Set a section visibility directly
    const setSection = useCallback((section, visible) => {
        setPreferences(prev => ({
            ...prev,
            sectionVisibility: {
                ...prev.sectionVisibility,
                [section]: visible
            },
            lastUpdated: new Date().toISOString()
        }));
    }, []);
    // Reset to default (show all)
    const resetToDefaults = useCallback(() => {
        setPreferences({
            sectionVisibility: DEFAULT_SECTION_VISIBILITY,
            lastUpdated: new Date().toISOString()
        });
    }, []);
    // Show all sections
    const showAllSections = useCallback(() => {
        setPreferences(prev => ({
            ...prev,
            sectionVisibility: {
                stats: true,
                charts: true,
                criticalActions: true,
                highPriorityActions: true,
                mediumActions: true,
                pendingApprovals: true
            },
            lastUpdated: new Date().toISOString()
        }));
    }, []);
    // Hide all sections (except one - can't hide everything)
    const hideAllSections = useCallback(() => {
        setPreferences(prev => ({
            ...prev,
            sectionVisibility: {
                stats: true, // Always keep at least stats visible
                charts: false,
                criticalActions: false,
                highPriorityActions: false,
                mediumActions: false,
                pendingApprovals: false
            },
            lastUpdated: new Date().toISOString()
        }));
    }, []);
    // Check if a specific section is visible
    const isSectionVisible = useCallback((section) => {
        return preferences.sectionVisibility[section] ?? true;
    }, [preferences.sectionVisibility]);
    // Get count of visible sections
    const visibleSectionCount = Object.values(preferences.sectionVisibility).filter(Boolean).length;
    const totalSectionCount = Object.keys(preferences.sectionVisibility).length;
    return {
        preferences,
        toggleSection,
        setSection,
        resetToDefaults,
        showAllSections,
        hideAllSections,
        isSectionVisible,
        visibleSectionCount,
        totalSectionCount
    };
};
export default useDashboardPreferences;
